import { ATHLETE_MANAGER_PAGE_SIZE_DEFAULT, ATHLETE_PROBLEMS, type AthleteProblem } from "@seagames/contracts";
import { z } from "zod";
import { cleanText, idParam, pageParam, sizeParam, textParam } from "../nocs/list-params";

/**
 * Athlete manager: the filters live in the URL (`/athlete-manager?q&noc&sport&problem&page&size`).
 * A bad value is dropped, never an error page. The API refuses a list with
 * no name, NOC or sport, so nothing is fetched until one is set (`ready`).
 */
export const PROBLEM_FILTERS = [...ATHLETE_PROBLEMS, "any"] as const;
export type ProblemFilter = (typeof PROBLEM_FILTERS)[number];

export const athleteManagerSearch = z.object({
	q: textParam,
	noc: idParam,
	sport: idParam,
	problem: z.enum(PROBLEM_FILTERS).optional().catch(undefined),
	page: pageParam,
	size: sizeParam,
});
export type AthleteManagerSearch = z.infer<typeof athleteManagerSearch>;

export interface AthleteManagerDraft extends Record<string, unknown> {
	q: string;
	noc: string;
	sport: string;
	problem: ProblemFilter | "";
}

export const EMPTY_ATHLETE_MANAGER_DRAFT: AthleteManagerDraft = { q: "", noc: "", sport: "", problem: "" };

export const draftFrom = (s: AthleteManagerSearch): AthleteManagerDraft => ({
	q: s.q ?? "",
	noc: s.noc ?? "",
	sport: s.sport ?? "",
	problem: s.problem ?? "",
});

/** Draft → URL: empties dropped, the page starts over, the page size stays. */
export const searchFrom = (d: AthleteManagerDraft, size: number | undefined): AthleteManagerSearch => ({
	q: cleanText(d.q),
	noc: d.noc || undefined,
	sport: d.sport || undefined,
	problem: d.problem || undefined,
	size,
});

export const filterCount = (s: AthleteManagerSearch): number => [s.q, s.noc, s.sport, s.problem].filter((v) => v !== undefined).length;

/** The API answers only with a name, an NOC or a sport. */
export const isReady = (s: AthleteManagerSearch): boolean => s.q !== undefined || s.noc !== undefined || s.sport !== undefined;

export const paging = (s: AthleteManagerSearch) => ({ page: s.page ?? 1, pageSize: s.size ?? ATHLETE_MANAGER_PAGE_SIZE_DEFAULT });

/** The `GET /athlete-manager` query — snake_case, as the wire wants it. */
export interface AthleteManagerParams {
	games_id: string;
	search?: string;
	responsible_organization_id?: string;
	sport_id?: string;
	problem?: AthleteProblem | "any";
	page: number;
	page_size: number;
}

export function athleteManagerParams(gamesId: string, s: AthleteManagerSearch): AthleteManagerParams {
	const { page, pageSize } = paging(s);
	return {
		games_id: gamesId,
		search: s.q,
		responsible_organization_id: s.noc,
		sport_id: s.sport,
		problem: s.problem,
		page,
		page_size: pageSize,
	};
}
